/**
 * Shared axios instance for the HRIS frontend.
 *
 * Key hardening applied here (all other call sites inherit these settings):
 *   • Default timeout of 30 s covers normal API latency including the proxy → NestJS/Go hop.
 *     Calls that genuinely need longer (e.g. report exports) can pass a per-request override:
 *
 *       BaseAPI.someExport({ timeout: 120_000 })  // 2 min for report generation
 *
 *     Any AxiosRequestConfig field passed to request() is spread into axiosConfig, so the
 *     per-request timeout automatically overrides this default.
 *
 *   • Global response interceptor that surfaces network / HTTP errors as SweetAlert2 toasts,
 *     matching the project-wide `dialogUtils` / `useConfirmDialog` UX pattern.
 *
 * Debounce strategy:
 *   A burst of failed requests (e.g. a list page with 10 parallel GETs hitting a dead backend)
 *   will only fire one toast per unique message in a 5-second window.  We store the last-shown
 *   timestamp per message key in a plain Map; no external dep required.
 *
 * Error routing:
 *   ┌────────────────────────────────────────────────────────────────────────────────┐
 *   │ Condition                          │ Action                                    │
 *   ├────────────────────────────────────┼───────────────────────────────────────────┤
 *   │ Network / ETIMEDOUT / status 0     │ Toast "Network problem…"                  │
 *   │ AbortError / CanceledError         │ Suppress (deliberate cancellation)        │
 *   │ 401                                │ Let existing auth-redirect logic handle   │
 *   │ 403                                │ Toast "You don't have permission…"        │
 *   │ 5xx                                │ Toast "Server error…"                     │
 *   │ Everything else                    │ Pass through silently                     │
 *   └────────────────────────────────────┴───────────────────────────────────────────┘
 *
 * IMPORTANT: The interceptor always re-throws the error so per-call .catch() / try-catch
 * handlers still run.  The toast is informational only; it never swallows the error.
 */

import Axios, { AxiosInstance, AxiosResponse } from 'axios';
import Swal from 'sweetalert2';
import { DIALOG_COLORS } from '../utils/dialogConfig';

// ─── Debounce helpers ──────────────────────────────────────────────────────────

/** De-dupe window in ms: same message shown at most once per this interval. */
const TOAST_DEDUPE_WINDOW_MS = 5_000;

/** Map<messageKey, lastShownTimestamp> */
const lastToastTimestamp = new Map<string, number>();

/**
 * Fire a Swal error toast, but deduplicate within TOAST_DEDUPE_WINDOW_MS.
 * Returns true if the toast was actually shown, false if suppressed.
 */
function debouncedErrorToast(title: string, text: string): boolean {
  const key = `${title}||${text}`;
  const now = Date.now();
  const last = lastToastTimestamp.get(key) ?? 0;

  if (now - last < TOAST_DEDUPE_WINDOW_MS) {
    return false; // suppress duplicate
  }

  lastToastTimestamp.set(key, now);

  Swal.fire({
    icon: 'error',
    title,
    text,
    confirmButtonColor: DIALOG_COLORS.confirm.danger,
    // Auto-close after 6 s so it doesn't block the user indefinitely
    timer: 6_000,
    timerProgressBar: true,
    showConfirmButton: false,
  });

  return true;
}

// ─── Axios instance ────────────────────────────────────────────────────────────

/**
 * Named axios instance shared by BaseAPI and any other API module that needs
 * authenticated requests.  Using a named instance (rather than the global Axios
 * default) means we can attach interceptors without affecting third-party libs
 * that also import axios (e.g. S3 PUT requests which must NOT send our auth headers).
 */
const axiosInstance: AxiosInstance = Axios.create({
  // 30 s covers normal API latency including the Traefik → backend hop.
  // Override per-request for long-running operations (reports, bulk exports, etc.):
  //   axiosInstance.request({ ..., timeout: 120_000 })
  timeout: 30_000,
});

// ─── Response interceptor ─────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  // Success path — pass through unchanged
  (response: AxiosResponse) => response,

  // Error path — typed as `unknown` because that is what axios interceptors receive in
  // strict TypeScript; we use Axios.isAxiosError() to narrow to AxiosError<unknown>.
  (error: unknown) => {
    // 1. Deliberate cancellation (AbortController / axios CancelToken) — suppress silently.
    if (Axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Narrow to AxiosError for the rest of the handler.
    if (!Axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const code   = error.code; // ETIMEDOUT, ERR_NETWORK, ECONNABORTED, etc.

    // 2. Network-level errors (no response received, or timeout).
    if (!error.response || code === 'ETIMEDOUT' || code === 'ERR_NETWORK' || code === 'ECONNABORTED') {
      debouncedErrorToast(
        'Network Problem',
        'Please check your connection and try again.'
      );
      return Promise.reject(error);
    }

    // 3. 401 Unauthorized — passed through without a toast.
    //    A global redirect fires in AxiosInterceptor.tsx (useAxiosInterceptor hook)
    //    after MAX_CONSECUTIVE_UNAUTHORIZED (5) back-to-back 401s, sending the user
    //    to /login (or /portal/login for the client portal). Individual call sites
    //    may also handle 401 in their own .catch() blocks.
    if (status === 401) {
      return Promise.reject(error);
    }

    // 4. 403 Forbidden
    if (status === 403) {
      debouncedErrorToast(
        'Permission Denied',
        "You don't have permission to do that."
      );
      return Promise.reject(error);
    }

    // 5. 5xx Server errors
    if (status !== undefined && status >= 500) {
      debouncedErrorToast(
        'Server Error',
        "We've been notified. Please try again in a moment."
      );
      return Promise.reject(error);
    }

    // 6. Everything else (4xx other than 401/403) — pass through silently so
    //    per-call handlers can render field-level validation errors, etc.
    return Promise.reject(error);
  }
);

export default axiosInstance;
